import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  ShadingType,
  ImageRun,
  TableLayoutType,
  PageBreak,
  convertInchesToTwip,
} from "docx";
import { saveAs } from "file-saver";
import logoSrc from "../assets/logo.png";

// --- Types ---

interface FieldEdit {
  field: string;
  originalValue: any;
  newValue: any;
}

interface DaySchedule {
  title?: string;
  description?: string;
  attendance?: string;
  attendanceNumber?: string;
  setupAccess?: string;
  eventStart?: string;
  eventEnd?: string;
  spaces?: string[];
  kitchenHours?: string;
  alcohol?: boolean;
  additionalServices?: string[];
  notes?: string;
}

interface ContractSubmission {
  data: Record<string, any>;
  edits: FieldEdit[];
  submittedAt: Date | null;
}

// --- Brand Colors ---
const COLORS = {
  primary: "4A7C2E", // Forest green
  primaryDark: "3A6424",
  accent: "DE6E49", // Orange accent
  accentLight: "FFEEE9",
  headerBg: "F5F9F3", // Light green tint
  sectionBg: "FAFAFA",
  tableBorder: "E0E0E0",
  textDark: "1A1A1A",
  textMuted: "666666",
  white: "FFFFFF",
  warningBg: "FFF8E1",
  warningBorder: "FFB300",
} as const;

// --- Helpers ---

/** Resolve a field value, preferring the latest admin edit */
function resolve(sub: ContractSubmission, fieldKey: string): string {
  const fieldEdits = sub.edits?.filter((e) => e.field === fieldKey);
  const lastEdit = fieldEdits?.length
    ? fieldEdits[fieldEdits.length - 1]
    : undefined;
  const raw = lastEdit ? lastEdit.newValue : sub.data[fieldKey];
  if (raw === null || raw === undefined || raw === "") return "";
  if (Array.isArray(raw)) return raw.join(", ");
  return String(raw);
}

/** Get the day of the week from a date string */
function getDayOfWeek(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { weekday: "long" });
}

/** Format a date string for display */
function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Format time to AM/PM */
function formatTimeToAMPM(time: string): string {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const hour24 = parseInt(hours);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? "PM" : "AM";
  return `${hour12}:${minutes} ${ampm}`;
}

// Border styles
const modernBorder = {
  top: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
  bottom: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
  left: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
  right: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
} as const;

const noBorder = {
  top: { style: BorderStyle.NONE, size: 0, color: COLORS.white },
  bottom: { style: BorderStyle.NONE, size: 0, color: COLORS.white },
  left: { style: BorderStyle.NONE, size: 0, color: COLORS.white },
  right: { style: BorderStyle.NONE, size: 0, color: COLORS.white },
} as const;

const accentTopBorder = {
  top: { style: BorderStyle.SINGLE, size: 24, color: COLORS.accent },
  bottom: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
  left: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
  right: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
} as const;

/** Create a modern section header */
function createSectionHeader(
  title: string,
  icon?: string,
  colSpan: number = 2
): TableRow {
  return new TableRow({
    children: [
      new TableCell({
        borders: {
          top: { style: BorderStyle.SINGLE, size: 24, color: COLORS.primary },
          bottom: { style: BorderStyle.NONE, size: 0, color: COLORS.white },
          left: { style: BorderStyle.NONE, size: 0, color: COLORS.white },
          right: { style: BorderStyle.NONE, size: 0, color: COLORS.white },
        },
        columnSpan: colSpan,
        shading: { type: ShadingType.SOLID, color: COLORS.headerBg },
        margins: {
          top: convertInchesToTwip(0.15),
          bottom: convertInchesToTwip(0.15),
          left: convertInchesToTwip(0.2),
          right: convertInchesToTwip(0.2),
        },
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: icon ? `${icon}  ` : "",
                size: 24,
                font: "Segoe UI Emoji",
              }),
              new TextRun({
                text: title.toUpperCase(),
                bold: true,
                size: 22,
                font: "Calibri",
                color: COLORS.primary,
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

/** Create a label cell */
function labelCell(
  text: string,
  widthPct?: number,
  options?: { bold?: boolean; color?: string }
): TableCell {
  return new TableCell({
    borders: modernBorder,
    shading: { type: ShadingType.SOLID, color: COLORS.sectionBg },
    width: widthPct
      ? { size: widthPct, type: WidthType.PERCENTAGE }
      : undefined,
    margins: {
      top: convertInchesToTwip(0.08),
      bottom: convertInchesToTwip(0.08),
      left: convertInchesToTwip(0.15),
      right: convertInchesToTwip(0.1),
    },
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text,
            bold: options?.bold ?? true,
            size: 20,
            font: "Calibri",
            color: options?.color ?? COLORS.textDark,
          }),
        ],
      }),
    ],
  });
}

/** Create a value cell */
function valueCell(
  text: string,
  widthPct?: number,
  options?: { bold?: boolean; color?: string; highlight?: boolean }
): TableCell {
  return new TableCell({
    borders: modernBorder,
    shading: options?.highlight
      ? { type: ShadingType.SOLID, color: COLORS.accentLight }
      : undefined,
    width: widthPct
      ? { size: widthPct, type: WidthType.PERCENTAGE }
      : undefined,
    margins: {
      top: convertInchesToTwip(0.08),
      bottom: convertInchesToTwip(0.08),
      left: convertInchesToTwip(0.15),
      right: convertInchesToTwip(0.1),
    },
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: text || "—",
            bold: options?.bold ?? false,
            size: 20,
            font: "Calibri",
            color: options?.color ?? COLORS.textDark,
          }),
        ],
      }),
    ],
  });
}

/** Create a simple label-value row */
function fieldRow(label: string, value: string, highlight?: boolean): TableRow {
  return new TableRow({
    children: [labelCell(label, 35), valueCell(value, 65, { highlight })],
  });
}

/** Create an empty spacer row */
function spacerRow(colSpan: number = 2): TableRow {
  return new TableRow({
    children: [
      new TableCell({
        borders: noBorder,
        columnSpan: colSpan,
        children: [new Paragraph({ children: [] })],
      }),
    ],
  });
}

/** Create a day schedule card for multi-day events */
function createDayScheduleSection(
  dayNumber: number,
  dateLabel: string,
  schedule: DaySchedule
): Table {
  const rows: TableRow[] = [
    // Day header with accent
    new TableRow({
      children: [
        new TableCell({
          borders: accentTopBorder,
          columnSpan: 2,
          shading: { type: ShadingType.SOLID, color: COLORS.accentLight },
          margins: {
            top: convertInchesToTwip(0.1),
            bottom: convertInchesToTwip(0.1),
            left: convertInchesToTwip(0.15),
            right: convertInchesToTwip(0.15),
          },
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: `Day ${dayNumber}: `,
                  bold: true,
                  size: 22,
                  font: "Calibri",
                  color: COLORS.accent,
                }),
                new TextRun({
                  text: dateLabel,
                  bold: true,
                  size: 22,
                  font: "Calibri",
                  color: COLORS.textDark,
                }),
              ],
            }),
          ],
        }),
      ],
    }),
  ];

  // Add schedule details
  if (schedule.title) {
    rows.push(fieldRow("Event Title", schedule.title));
  }
  if (schedule.description) {
    rows.push(fieldRow("Description", schedule.description));
  }
  if (schedule.attendance) {
    rows.push(fieldRow("Attendance", schedule.attendance));
  }
  if (schedule.setupAccess) {
    rows.push(fieldRow("Setup Access", schedule.setupAccess));
  }
  if (schedule.eventStart || schedule.eventEnd) {
    const timeRange = [schedule.eventStart, schedule.eventEnd]
      .filter(Boolean)
      .join(" - ");
    rows.push(fieldRow("Event Time", timeRange));
  }
  if (schedule.spaces && schedule.spaces.length > 0) {
    rows.push(fieldRow("Rental Spaces", schedule.spaces.join(", ")));
  }
  if (schedule.kitchenHours) {
    rows.push(fieldRow("Kitchen Hours", `${schedule.kitchenHours} hours`));
  }
  if (schedule.alcohol !== undefined) {
    rows.push(fieldRow("Alcohol", schedule.alcohol ? "Yes" : "No"));
  }
  if (schedule.additionalServices && schedule.additionalServices.length > 0) {
    rows.push(
      fieldRow("Additional Services", schedule.additionalServices.join(", "))
    );
  }
  if (schedule.notes) {
    rows.push(fieldRow("Notes", schedule.notes));
  }

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.FIXED,
    rows,
  });
}

// Fetch the logo as an ArrayBuffer
let logoBufferPromise: Promise<ArrayBuffer> | null = null;

function fetchLogo(): Promise<ArrayBuffer> {
  if (!logoBufferPromise) {
    logoBufferPromise = fetch(logoSrc).then((r) => r.arrayBuffer());
  }
  return logoBufferPromise;
}

// --- Main Export ---

export async function generateContractV2(
  sub: ContractSubmission
): Promise<void> {
  const logoBuffer = await fetchLogo();

  // Resolve all relevant fields
  const client = resolve(sub, "Client/Organization Name");
  const contactName = resolve(sub, "Client Contact Name");
  const role = resolve(sub, "Role/Title");
  const phone = resolve(sub, "Phone Number");
  const email = resolve(sub, "Email Address");
  const address = resolve(sub, "Billing Address");
  const orgType = resolve(sub, "Organization Type");

  const eventDate = resolve(sub, "Event Date");
  const eventDates = resolve(sub, "Event Dates");
  const selectedDates = resolve(sub, "Selected Dates");
  const eventType = resolve(sub, "Event Type");
  const eventSchedule = resolve(sub, "Event Schedule");
  const shortTitle = resolve(sub, "Short Event Title");
  const description = resolve(sub, "Brief Description");
  const attendance = resolve(sub, "Anticipated Attendance");
  const rentalSpace = resolve(sub, "Rental Space Requested");
  const setupTime = resolve(sub, "Setup Access Time");
  const startTime = resolve(sub, "Event Start Time");
  const endTime = resolve(sub, "Event End Time");
  const notes = resolve(sub, "Notes");
  const alcohol = resolve(sub, "Will alcohol be included?");
  const kitchenHours = resolve(sub, "Kitchen Hours");
  const additionalServices = resolve(sub, "Additional Services Requested");

  // Determine display date based on event type
  let displayDate = eventDate;
  let isMultiDay = false;
  let isRecurring = false;

  if (eventDates) {
    displayDate = eventDates;
    isMultiDay = true;
  } else if (selectedDates) {
    displayDate = selectedDates;
    isMultiDay = true;
  } else if (eventSchedule) {
    displayDate = eventSchedule;
    isRecurring = true;
  }

  // Check for per-day schedules
  const perDaySchedules: { label: string; schedule: DaySchedule }[] = [];
  Object.keys(sub.data).forEach((key) => {
    if (key.startsWith("Day ") && typeof sub.data[key] === "object") {
      const dayData = sub.data[key] as DaySchedule;
      perDaySchedules.push({
        label: key,
        schedule: dayData,
      });
    }
  });

  const dayOfWeek = getDayOfWeek(eventDate || "");

  // Generate agreement number
  const now = new Date();
  const initials = contactName
    .split(" ")
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  const agreementNumber = `${initials}/${String(now.getMonth() + 1).padStart(2, "0")}/${String(now.getDate()).padStart(2, "0")}/${String(now.getFullYear()).slice(-2)}/1`;

  // --- Build Document Sections ---
  const documentChildren: (Paragraph | Table)[] = [];

  // ===== HEADER =====
  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: [
        new TableRow({
          children: [
            // Logo column
            new TableCell({
              borders: noBorder,
              width: { size: 18, type: WidthType.PERCENTAGE },
              verticalAlign: "center",
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: logoBuffer,
                      transformation: { width: 85, height: 56 },
                      type: "png",
                    }),
                  ],
                }),
              ],
            }),
            // Company info column
            new TableCell({
              borders: noBorder,
              width: { size: 82, type: WidthType.PERCENTAGE },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "St. John's Farmers' Market Co-operative",
                      bold: true,
                      size: 32,
                      color: COLORS.primary,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  spacing: { before: 60 },
                  children: [
                    new TextRun({
                      text: "245 Freshwater Road, St. John's, NL  A1B 1B3",
                      size: 20,
                      font: "Calibri",
                      color: COLORS.textMuted,
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "eventsmanager@sjfm.ca  |  www.sjfm.ca  |  (709) 221-7536",
                      size: 20,
                      font: "Calibri",
                      color: COLORS.textMuted,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    })
  );

  // Divider line
  documentChildren.push(
    new Paragraph({
      spacing: { before: 200, after: 100 },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 16, color: COLORS.primary },
      },
      children: [],
    })
  );

  // ===== TITLE =====
  documentChildren.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: 100 },
      children: [
        new TextRun({
          text: "EVENT RENTAL AGREEMENT",
          bold: true,
          size: 40,
          font: "Calibri",
          color: COLORS.textDark,
        }),
      ],
    })
  );

  documentChildren.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
      children: [
        new TextRun({
          text: "Agreement #: ",
          size: 20,
          font: "Calibri",
          color: COLORS.textMuted,
        }),
        new TextRun({
          text: agreementNumber,
          bold: true,
          size: 20,
          font: "Calibri",
          color: COLORS.accent,
        }),
      ],
    })
  );

  // ===== PREAMBLE =====
  documentChildren.push(
    new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 100 },
      children: [
        new TextRun({
          text: "The St. John's Community Market is owned by the City of St. John's and managed full-time by the St. John's Farmers' Market Cooperative. This document constitutes a binding agreement between the renter and the St. John's Farmers' Market Cooperative for the rental of community spaces at the facility.",
          size: 20,
          font: "Calibri",
          color: COLORS.textMuted,
        }),
      ],
    })
  );

  documentChildren.push(new Paragraph({ spacing: { before: 200 } }));

  // ===== SECTION 1: CLIENT INFORMATION =====
  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: [
        createSectionHeader("Client Information", ""),
        fieldRow("Organization / Client Name", client, true),
        fieldRow("Primary Contact", contactName),
        fieldRow("Role / Title", role),
        fieldRow("Phone", phone),
        fieldRow("Email", email),
        fieldRow("Billing Address", address),
        fieldRow("Organization Type", orgType, true),
      ],
    })
  );

  documentChildren.push(new Paragraph({ spacing: { before: 250 } }));

  // ===== SECTION 2: EVENT OVERVIEW =====
  const eventOverviewRows: TableRow[] = [createSectionHeader("Event Overview", "")];

  // Event title and description
  if (shortTitle) {
    eventOverviewRows.push(fieldRow("Event Title", shortTitle, true));
  }
  if (description) {
    eventOverviewRows.push(fieldRow("Description", description));
  }

  // Date information based on event type
  if (isRecurring) {
    eventOverviewRows.push(
      fieldRow("Event Schedule", eventSchedule || displayDate, true)
    );
    eventOverviewRows.push(fieldRow("Event Type", "Recurring Event"));
  } else if (isMultiDay) {
    eventOverviewRows.push(fieldRow("Event Type", eventType || "Multi-Day Event"));
    if (eventDates) {
      eventOverviewRows.push(fieldRow("Event Dates", eventDates, true));
    }
    if (selectedDates && !eventDates) {
      eventOverviewRows.push(fieldRow("Selected Dates", selectedDates, true));
    }
  } else if (eventDate) {
    eventOverviewRows.push(fieldRow("Event Date", formatDateDisplay(eventDate), true));
    if (dayOfWeek) {
      eventOverviewRows.push(fieldRow("Day of Week", dayOfWeek));
    }
  }

  // Attendance
  if (attendance) {
    eventOverviewRows.push(fieldRow("Expected Attendance", attendance));
  }

  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: eventOverviewRows,
    })
  );

  documentChildren.push(new Paragraph({ spacing: { before: 250 } }));

  // ===== SECTION 3: SCHEDULE & SPACES =====
  // Only show this section if not customizing per day
  if (perDaySchedules.length === 0) {
    const scheduleRows: TableRow[] = [
      createSectionHeader("Schedule & Rental Spaces", ""),
    ];

    if (setupTime) {
      scheduleRows.push(fieldRow("Setup Access Time", formatTimeToAMPM(setupTime)));
    }
    if (startTime) {
      scheduleRows.push(fieldRow("Event Start Time", formatTimeToAMPM(startTime)));
    }
    if (endTime) {
      scheduleRows.push(fieldRow("Event End Time", formatTimeToAMPM(endTime)));
    }
    scheduleRows.push(fieldRow("End of Takedown", "")); // For admin to fill

    if (rentalSpace) {
      scheduleRows.push(fieldRow("Rental Spaces", rentalSpace, true));
    }
    if (kitchenHours) {
      scheduleRows.push(fieldRow("Commercial Kitchen Hours", `${kitchenHours} hours`));
    }
    if (alcohol) {
      scheduleRows.push(fieldRow("Alcohol Service", alcohol));
    }
    if (additionalServices) {
      scheduleRows.push(fieldRow("Additional Services", additionalServices));
    }
    if (notes) {
      scheduleRows.push(fieldRow("Special Notes", notes));
    }

    documentChildren.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        layout: TableLayoutType.FIXED,
        rows: scheduleRows,
      })
    );

    documentChildren.push(new Paragraph({ spacing: { before: 250 } }));
  }

  // ===== PER-DAY SCHEDULES (if customized) =====
  if (perDaySchedules.length > 0) {
    documentChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "DAILY EVENT SCHEDULE",
            bold: true,
            size: 24,
            color: COLORS.primary,
            font: "Calibri",
          }),
        ],
        spacing: { after: 150 },
      })
    );

    perDaySchedules.forEach((day, index) => {
      // Extract date from label (e.g., "Day 1 (Mon, Jan 15)" -> date info)
      const dateMatch = day.label.match(/Day \d+.*?\((.*?)\)/);
      const dateLabel = dateMatch ? dateMatch[1] : day.label;

      documentChildren.push(
        createDayScheduleSection(index + 1, dateLabel, day.schedule)
      );
      documentChildren.push(new Paragraph({ spacing: { before: 150 } }));
    });
  }

  // ===== SECTION 4: COST =====
  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: [
        createSectionHeader("Cost Summary", "", 4),
        // Header row
        new TableRow({
          children: [
            labelCell("Description", 45),
            labelCell("Qty", 12),
            labelCell("Rate", 18),
            labelCell("Subtotal", 25),
          ],
        }),
        // Pre-fill first row with rental space
        new TableRow({
          children: [
            valueCell(rentalSpace || "", 45),
            valueCell("", 12),
            valueCell("", 18),
            valueCell("", 25),
          ],
        }),
        // Kitchen hours row if applicable
        ...(kitchenHours
          ? [
              new TableRow({
                children: [
                  valueCell("Commercial Kitchen Usage", 45),
                  valueCell(kitchenHours, 12),
                  valueCell(
                    orgType === "Non-Profit" ? "$27.50/hr" : "$41.00/hr",
                    18
                  ),
                  valueCell("", 25),
                ],
              }),
            ]
          : []),
        // Additional services
        ...(additionalServices
          ? [
              new TableRow({
                children: [
                  valueCell(additionalServices, 45),
                  valueCell("", 12),
                  valueCell("", 18),
                  valueCell("", 25),
                ],
              }),
            ]
          : []),
        // Empty rows for additions
        new TableRow({
          children: [
            valueCell("", 45),
            valueCell("", 12),
            valueCell("", 18),
            valueCell("", 25),
          ],
        }),
        new TableRow({
          children: [
            valueCell("", 45),
            valueCell("", 12),
            valueCell("", 18),
            valueCell("", 25),
          ],
        }),
        // Subtotal
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.sectionBg },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: "Subtotal",
                      bold: true,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
            valueCell("$", 25),
          ],
        }),
        // HST
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.sectionBg },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: "HST (15%)",
                      bold: true,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
            valueCell("$", 25),
          ],
        }),
        // Total
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.accentLight },
              margins: {
                top: convertInchesToTwip(0.1),
                bottom: convertInchesToTwip(0.1),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: "TOTAL RENTAL FEE",
                      bold: true,
                      size: 22,
                      font: "Calibri",
                      color: COLORS.accent,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.accentLight },
              margins: {
                top: convertInchesToTwip(0.1),
                bottom: convertInchesToTwip(0.1),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "$",
                      bold: true,
                      size: 22,
                      font: "Calibri",
                      color: COLORS.accent,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        spacerRow(4),
        // Payment schedule
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.warningBg },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: "Deposit (20%) - Due on Signing",
                      bold: true,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.warningBg },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [new TextRun({ text: "$", size: 20, font: "Calibri" })],
                }),
              ],
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.warningBg },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: "Balance - Due 30 Days Before Event",
                      bold: true,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              borders: modernBorder,
              shading: { type: ShadingType.SOLID, color: COLORS.warningBg },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [new TextRun({ text: "$", size: 20, font: "Calibri" })],
                }),
              ],
            }),
          ],
        }),
      ],
    })
  );

  // Page break before responsibilities
  documentChildren.push(
    new Paragraph({
      children: [new PageBreak()],
    })
  );

  // ===== SECTION 5: SERVICES & RESPONSIBILITIES =====
  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: [
        createSectionHeader("Services & Responsibilities", ""),
        // Parking
        new TableRow({
          children: [
            labelCell("Parking", 25),
            new TableCell({
              borders: modernBorder,
              width: { size: 75, type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Renter is responsible for parking management including directing attendees to allocated spaces and managing parking-related issues. Parking is at own risk. See attached parking plan.",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        // SJFM Responsibilities
        new TableRow({
          children: [
            labelCell("SJFM Staff Will Provide", 25),
            new TableCell({
              borders: modernBorder,
              width: { size: 75, type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.12),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  spacing: { after: 40 },
                  children: [
                    new TextRun({
                      text: "Keyholding:",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: " Doors unlocked/locked at stipulated times; general facilities support",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  spacing: { after: 40 },
                  children: [
                    new TextRun({
                      text: "Support Staff:",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: " Basic cleaning, garbage removal, restocking washrooms, setup/teardown assistance",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Equipment staging as per agreed layout (communicate requirements at least 1 week before event)",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        // Equipment Available
        new TableRow({
          children: [
            labelCell("Equipment Available", 25),
            new TableCell({
              borders: modernBorder,
              width: { size: 75, type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.12),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  spacing: { after: 30 },
                  children: [
                    new TextRun({
                      text: "Tables: ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "38 rectangular folding (2.5'x6'), 6 rolling wooden, 12 square cafe-style",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  spacing: { after: 30 },
                  children: [
                    new TextRun({
                      text: "Chairs: ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "140 plastic grey, 50 laminate wood (community room only)",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  spacing: { after: 30 },
                  children: [
                    new TextRun({
                      text: "PA System: ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "Microphone, stand, 2 speakers, 5-channel mixer",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  spacing: { after: 30 },
                  children: [
                    new TextRun({
                      text: "Kitchen (if rented): ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "Gas oven, stove, grill, proofer, microwave, dishwasher, fridge, 100 place settings",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Pipe & Drape: ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "Available at additional charge with 1 week notice",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        // Renter Responsibilities
        new TableRow({
          children: [
            labelCell("Renter Responsibilities", 25),
            new TableCell({
              borders: modernBorder,
              width: { size: 75, type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.12),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                ...[
                  "Event promotion (use '245 Freshwater Road' as location, not 'Farmers' Market')",
                  "Communicate layout requirements at least 1 week before event",
                  "Setup and takedown of all equipment and decorations",
                  "Compliance with food safety, music licensing, fire safety, and alcohol regulations",
                  "Ensure cleanliness and health/safety of attendees",
                  "Maintain required public liability insurance for the event",
                ].map(
                  (item) =>
                    new Paragraph({
                      spacing: { after: 30 },
                      children: [
                        new TextRun({
                          text: `• ${item}`,
                          size: 18,
                          font: "Calibri",
                        }),
                      ],
                    })
                ),
              ],
            }),
          ],
        }),
        // Catering
        new TableRow({
          children: [
            labelCell("Catering", 25),
            new TableCell({
              borders: modernBorder,
              width: { size: 75, type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Renter arranges food vendors. Non-SJFM vendors must be compliant with all food safety requirements. Coffeeshop: Contact Cheddars Cafe directly if desired.",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        fieldRow("Alcohol Service", alcohol || "No"),
        fieldRow("Keyholding", "SJFM"),
      ],
    })
  );

  documentChildren.push(new Paragraph({ spacing: { before: 250 } }));

  // ===== SECTION 6: CANCELLATION POLICY =====
  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: [
        createSectionHeader("Cancellation Policy", ""),
        new TableRow({
          children: [
            labelCell("If Renter Cancels", 25),
            new TableCell({
              borders: modernBorder,
              width: { size: 75, type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.12),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  spacing: { after: 40 },
                  children: [
                    new TextRun({
                      text: "• Half-hall, cafe, kitchen, community room: ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "Full refund up to 14 days before event",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  spacing: { after: 40 },
                  children: [
                    new TextRun({
                      text: "• Full-hall rentals: ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "Full refund up to 30 days before event with written notice",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "• After these deadlines: ",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                    }),
                    new TextRun({
                      text: "80% refund (deposit is non-refundable)",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        new TableRow({
          children: [
            labelCell("If SJFM Cancels", 25),
            new TableCell({
              borders: modernBorder,
              width: { size: 75, type: WidthType.PERCENTAGE },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "In case of significant unforeseen circumstances or emergency, renter will be notified promptly and receive a full refund.",
                      size: 18,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    })
  );

  documentChildren.push(new Paragraph({ spacing: { before: 250 } }));

  // ===== ADDENDUM NOTICE =====
  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: [
        new TableRow({
          children: [
            new TableCell({
              borders: {
                top: { style: BorderStyle.SINGLE, size: 16, color: COLORS.warningBorder },
                bottom: { style: BorderStyle.SINGLE, size: 16, color: COLORS.warningBorder },
                left: { style: BorderStyle.SINGLE, size: 16, color: COLORS.warningBorder },
                right: { style: BorderStyle.SINGLE, size: 16, color: COLORS.warningBorder },
              },
              shading: { type: ShadingType.SOLID, color: COLORS.warningBg },
              margins: {
                top: convertInchesToTwip(0.15),
                bottom: convertInchesToTwip(0.15),
                left: convertInchesToTwip(0.2),
                right: convertInchesToTwip(0.2),
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text: "By signing this agreement, the renter acknowledges they have read and agree with the facility usage rules and expectations in Addendum 1 to this agreement.",
                      bold: true,
                      italics: true,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    })
  );

  documentChildren.push(new Paragraph({ spacing: { before: 300 } }));

  // ===== SECTION 7: SIGNATURES =====
  documentChildren.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
      rows: [
        createSectionHeader("Signatures", ""),
        // Renter signature
        new TableRow({
          children: [
            new TableCell({
              borders: modernBorder,
              columnSpan: 2,
              margins: {
                top: convertInchesToTwip(0.1),
                bottom: convertInchesToTwip(0.5),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "RENTER SIGNATURE",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                      color: COLORS.textMuted,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        new TableRow({
          children: [
            labelCell("Print Name", 30),
            valueCell("", 70),
          ],
        }),
        new TableRow({
          children: [
            labelCell("Date", 30),
            valueCell("", 70),
          ],
        }),
        spacerRow(2),
        // SJFM signature
        new TableRow({
          children: [
            new TableCell({
              borders: modernBorder,
              columnSpan: 2,
              margins: {
                top: convertInchesToTwip(0.1),
                bottom: convertInchesToTwip(0.5),
                left: convertInchesToTwip(0.15),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "SJFM REPRESENTATIVE SIGNATURE",
                      bold: true,
                      size: 18,
                      font: "Calibri",
                      color: COLORS.textMuted,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        new TableRow({
          children: [
            labelCell("Print Name", 30),
            valueCell("", 70),
          ],
        }),
        new TableRow({
          children: [
            labelCell("Date", 30),
            valueCell("", 70),
          ],
        }),
      ],
    })
  );

  documentChildren.push(new Paragraph({ spacing: { before: 350 } }));

  // ===== FOOTER =====
  documentChildren.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      border: {
        top: { style: BorderStyle.SINGLE, size: 8, color: COLORS.tableBorder },
      },
      spacing: { before: 100 },
      children: [],
    })
  );

  documentChildren.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 150 },
      children: [
        new TextRun({
          text: "St. John's Farmers' Market Co-operative",
          bold: true,
          size: 24,
          color: COLORS.primary,
          font: "Calibri",
        }),
      ],
    })
  );

  documentChildren.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: "Business # 8266 15114 RT 0001",
          size: 18,
          font: "Calibri",
          color: COLORS.textMuted,
        }),
      ],
    })
  );

  documentChildren.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: "245 Freshwater Road, St. John's, NL  A1B 1B3  |  (709) 221-7536  |  eventsmanager@sjfm.ca",
          size: 18,
          font: "Calibri",
          color: COLORS.textMuted,
        }),
      ],
    })
  );

  // ===== BUILD DOCUMENT =====
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.6),
              right: convertInchesToTwip(0.6),
              bottom: convertInchesToTwip(0.5),
              left: convertInchesToTwip(0.6),
            },
          },
        },
        children: documentChildren,
      },
    ],
  });

  // Generate and download
  const blob = await Packer.toBlob(doc);
  const fileName = `Rental Agreement V2 - ${client || "Unknown"} - ${displayDate || "No Date"}.docx`;
  saveAs(blob, fileName);
}
